/**
 * Step 11 — Residence & Address
 *
 * Simplified: 3 fields per address (state, district, village/colony).
 * Fields: currentAddress, permanentAddress, homeNativePlace, ancestralPlace
 *
 * @module udp-schema/step-11
 */

import { z } from 'zod';

// ─── Address sub-schema (simplified) ─────────────────────────────────────────

/**
 * Area reference — saves BOTH the ObjectId and the display name (per the
 * "save both id + name" architectural decision). Three accepted shapes:
 *
 *   1. `{ _id, name, type? }` — selected from the master-db search API
 *      via `AreaCombobox`. The canonical case: we have a real Area entity
 *      reference (`_id`) AND its display name without an extra lookup.
 *   2. `{ name }` — free-text fallback for hyper-local names not seeded
 *      in the location DB (e.g. a specific colony / mohalla typed via
 *      the AreaCombobox `allowCreate` path). No `_id` exists to save.
 *   3. `string` — bare ObjectId, kept for backward compatibility with any
 *      legacy records that stored a flat _id reference. New saves will
 *      always use shape #1 or #2.
 *
 * `null` / `undefined` mean "not set" (e.g. cascade reset when parent
 * changes). Zod's default object behavior strips unknown keys, so any
 * extra metadata the API returned (e.g. `location`, `urbanBodyType`) is
 * dropped at save time — keeps stored documents clean and small.
 */
const areaRefSchema = z
  .union([
    z.string().trim(),
    z.object({
      _id: z.string().optional(),
      name: z.string(),
      type: z.string().optional(),
    }),
  ])
  .nullable()
  .optional();

export const simplifiedAddressSchema = z.object({
  /** State area ref */
  state: areaRefSchema,
  /** District area ref */
  district: areaRefSchema,
  /** Smallest area unit — village, colony, ward, etc. */
  village: areaRefSchema,
});

// ─── Main Step 11 Schema ────────────────────────────────────────────────────

export const step11ResidenceSchema = z.object({
  /** Current living address */
  currentAddress: simplifiedAddressSchema.optional(),

  /** Permanent address — may have "same as current" flag */
  permanentAddress: simplifiedAddressSchema
    .extend({
      sameAsCurrent: z.boolean().optional(),
    })
    .optional(),

  /** Home native place */
  homeNativePlace: simplifiedAddressSchema.optional(),

  /** Ancestral place */
  ancestralPlace: simplifiedAddressSchema.optional(),
});

// ─── Inferred types ─────────────────────────────────────────────────────────

export type SimplifiedAddressInput = z.infer<typeof simplifiedAddressSchema>;
export type Step11ResidenceInput = z.infer<typeof step11ResidenceSchema>;
